import Observable from '../../Utils/Observable';

import { isXSide } from '../Selectors/Side';
import { canWallSegmentGetObjectType } from '../Rules/canWallSegmentGetObjectType';
import { combineRangeObjects, isRangeObject, removeFromRange } from '../Selectors/RangeObject';
import getAddRule from '../Rules/getAddRule';
import { findNextFreeIndexForFixedSegmentObject, isFixedSegmentObject } from '../Selectors/StandaloneObject';
import {
  addChildObjectToParent,
  findParentForChildObject,
  isChildObject,
  removeFromParent,
} from '../Selectors/ChildObject';

/**
 * Converts the centerOffsetX local value (relative to segment) to absolute value (relative to the wall)
 * @param {object} object
 * @param {object} warehouseConfiguration
 * @return {number}
 */
function calculateAbsoluteCenterOffsetX(object, warehouseConfiguration) {
  if (object.absoluteOffset) {
    return object.centerOffsetX;
  }
  const {
    xWallSegmentWidth,
    zWallSegmentWidth,
    xWallSegmentCount,
    zWallSegmentCount,
    xWallSegmentsWidth,
    zWallSegmentsWidth,
  } = warehouseConfiguration;
  const segmentsWidth = isXSide(object.side) ? xWallSegmentsWidth : zWallSegmentsWidth;
  if (segmentsWidth?.length) {
    let sum = 0;
    for (let i = 0; i < object.index; i++) {
      sum += segmentsWidth[i];
    }
    return sum + object.centerOffsetX;
  }

  const segmentSize = isXSide(object.side) ? xWallSegmentWidth : zWallSegmentWidth;
  const segmentCount = isXSide(object.side) ? xWallSegmentCount : zWallSegmentCount;
  return segmentSize * object.index + object.centerOffsetX - (segmentCount - 1) * segmentSize * 0.5;
}

function findNextFreeIndexForRangeObject(warehouseConfiguration, object) {
  const { type, side, indexFrom, indexTo } = object;
  const { xWallSegmentCount, zWallSegmentCount } = warehouseConfiguration;
  const segmentCount = isXSide(object.side) ? xWallSegmentCount : zWallSegmentCount;
  for (let index = indexTo; index < segmentCount; index++) {
    if (canWallSegmentGetObjectType(warehouseConfiguration, type, side, index)) {
      return { index };
    }
  }
  for (let index = indexFrom; index >= 0; index--) {
    if (canWallSegmentGetObjectType(warehouseConfiguration, type, side, index)) {
      return { index };
    }
  }
  return false;
}

export const ChangeEvent = 'change';
export const ImmediatePropChangeEvent = 'immediatePropChange';
export const EventActions = {
  setProp: 'setProp',
  addObject: 'addObject',
  removeObject: 'removeObject',
  cloneObject: 'cloneObject',
  setData: 'setData',
};

export function createBuilding(state = {}, { calculateDerivedData, initialState = {} }) {
  state = { ...initialState, ...state };
  calculateDerivedData(state);
  const observable = new Observable();
  const { on, dispatch } = observable;

  const createSetter = (prop, customSetter) => (value) => {
    state[prop] = value;
    customSetter && customSetter(value, prop);
    dispatch(ImmediatePropChangeEvent, { prop, value, state });
    calculateDerivedData(state);
    dispatch(ChangeEvent, { action: EventActions.setProp, prop, value, state });
  };

  const setWidth = createSetter('width');
  const setLength = createSetter('length');
  const setHeight = createSetter('height');

  const getData = () => state;

  const onChange = (fn, propFilter = null) =>
    on(ChangeEvent, !propFilter ? fn : (obj) => propFilter.indexOf(obj.data.prop) !== -1 && fn(obj));

  const onImmediatePropChange = (fn) => on(ImmediatePropChangeEvent, fn);

  /**
   * Adds an object (type) to a wall segment (defined by side and index)
   * @param {string} type
   * @param {string} side
   * @param {number} index
   * @param {string} [variant]
   * @param {number || null} height
   * @param {*} option
   * @param {function || null} addRuleCallback
   */
  const addObject = async (
    type,
    side,
    index,
    variant,
    height = null,
    option = null,
    addRuleCallback = null
  ) => {
    if (!canWallSegmentGetObjectType(state, type, side, index, height)) {
      return;
    }
    const { objects } = state;
    const object = {
      type,
      side,
      index,
      variant,
      centerOffsetX: 0,
      centerOffsetY: 0,
      height,
      option,
    };

    const rule = getAddRule(state, object);
    addRuleCallback && (await addRuleCallback(rule));
    try {
      await rule?.applyRule();
    } catch (e) {
      console.error('Applying rule failed.', e);
      return;
    }

    if (isRangeObject(type)) {
      combineRangeObjects(objects, object);
    } else if (isChildObject(type)) {
      const parentObject = findParentForChildObject(objects, type, side, index);
      addChildObjectToParent(parentObject, object);
    } else {
      objects.push(object);
    }

    dispatch(ChangeEvent, { action: EventActions.addObject, prop: 'objects', objects, state, object });
  };

  const cloneObject = async (object, addRuleCallback) => {
    const { objects } = state;
    let clonedObject;

    if (isRangeObject(object.type)) {
      // if it's a range object, cloning means extending by one
      const index = findNextFreeIndexForRangeObject(state, object);
      if (index) {
        clonedObject = combineRangeObjects(objects, {
          ...object,
          ...index,
        });
      } else {
        throw new Error('No space left to clone object!');
      }
    } else if (isFixedSegmentObject(object.type)) {
      const index = findNextFreeIndexForFixedSegmentObject(state, object);
      if (!index) {
        throw new Error('No space left to clone object!');
      }
      clonedObject = {
        ...object,
        ...index,
      };
      const rule = getAddRule(state, clonedObject);
      addRuleCallback && (await addRuleCallback(rule));
      await rule?.applyRule();
      objects.push(clonedObject);
    } else {
      clonedObject = {
        ...object,
        index: null,
        absoluteOffset: true,
        centerOffsetX: calculateAbsoluteCenterOffsetX(object, state),
        children: object.children?.map((child) => ({ ...child })),
      };
      objects.push(clonedObject);
    }
    await Promise.all(
      dispatch(ChangeEvent, {
        action: EventActions.cloneObject,
        prop: 'objects',
        objects,
        state,
        object: clonedObject,
      })
    );
    return clonedObject;
  };

  const removeObject = (objectToRemove, options = {}) => {
    const { objects } = state;
    if (isRangeObject(objectToRemove.type)) {
      const { rangeIndex } = options;
      removeFromRange(objects, objectToRemove, rangeIndex);
    } else if (objectToRemove.isChild) {
      removeFromParent(objects, objectToRemove);
    } else {
      const index = objects.indexOf(objectToRemove);
      if (index !== -1) {
        objects.splice(index, 1);
      }
    }
    dispatch(ChangeEvent, { action: EventActions.removeObject, prop: 'objects', objects, state });
  };

  const setData = (data) => {
    state = {
      ...initialState,
      ...data,
    };
    calculateDerivedData(state);
    dispatch(ChangeEvent, { action: EventActions.setData, state });
  };

  return {
    setWidth,
    setLength,
    setHeight,

    addObject,
    removeObject,
    cloneObject,

    onChange,
    getData,
    setData,
    onImmediatePropChange,

    createSetter,
    on,
    dispatch,
  };
}

export default createBuilding;
